import { readBuildMetaNumber } from '@maat/utils';

// Structural, NOT imported from @module-federation/runtime: that is transitive
// via @module-federation/vite, not a declared dep, and pnpm resolves strictly.
interface AfterResolveArgs {
    remoteInfo?: {
        entry?: string;
    };
}

/**
 * Versions every remote-entry URL with the deployed build number. Browsers that
 * cached a remoteEntry.js under a year-long `immutable` (a since-fixed deploy bug)
 * never revalidate that SUBRESOURCE, not even on reload, and boot forever against
 * chunks that no longer exist — no server or CDN fix can reach them.
 *
 * The number is read at RUNTIME from the stamped <meta name="build-number">, so Nx
 * caching is unaffected; unstamped HTML no-ops. `afterResolve` is the hook for
 * `type: 'module'` remotes (createScript never fires for ESM entries).
 *
 * Keep the `?v=` shape in sync with `maat-preload-guest-remote` in vite.config.ts.
 */
export default function mfVersionedEntryPlugin() {
    return {
        name: 'maat-versioned-remote-entry',
        afterResolve<T extends AfterResolveArgs>(args: T): T {
            const buildNumber = readBuildMetaNumber('build-number');
            if (buildNumber === undefined) return args;
            const entry = args.remoteInfo?.entry;
            // afterResolve runs on every loadRemote and the same remoteInfo flows
            // back through, so skip an entry that is already versioned.
            if (!entry || entry.includes('?')) return args;
            args.remoteInfo!.entry = `${entry}?v=${buildNumber}`;
            return args;
        },
    };
}
